<template>
  <div>
    <h1>Input 输入框</h1>
    <p class="page-desc">通过鼠标或键盘输入内容，是最基础的表单域的包装。</p>

    <k-row gutter="16">
      <k-col span="24" :xl="{ span: 12 }">
        <demo-viewer
          title="基本用法"
          :code="JCYF"
          desc="可以使用 v-model 实现数据的双向绑定。"
        >
          <p>{{ value1 }}</p>
          <p>
            <k-input
              v-model="value1"
              placeholder="请输入.."
              style="width:300px;"
            ></k-input>
          </p>
        </demo-viewer>

        <demo-viewer
          title="带Icon的输入框"
          :code="ICON"
          desc="通过 icon 属性可以在输入框右边加一个图标。"
        >
          <p>
            <k-input
              icon="search"
              placeholder="请输入.."
              @on-icon-click="iconClick"
              style="width:300px;"
            ></k-input>
          </p>
        </demo-viewer>

        <demo-viewer
          title="带移除图标"
          :code="DYCTB"
          desc="带移除图标的输入框，点击图标删除所有内容。"
        >
          <p>
            <k-input
              clearable
              placeholder="请输入.."
              style="width:300px;"
            ></k-input>
          </p>
        </demo-viewer>

        <demo-viewer title="密码框" :code="DYCTB" desc="显示切换密码图标。">
          <p>
            <k-input
              placeholder="请输入.."
              showPwd
              style="width:300px;"
            ></k-input>
          </p>
        </demo-viewer>

        <demo-viewer
          title="圆角"
          :code="YJ"
          desc="circle 属性将使输入框设置为大圆角。"
        >
          <p>
            <k-input
              circle
              icon="message"
              placeholder="请输入.."
              style="width:300px;"
            ></k-input>
          </p>
          <p>
            <k-input
              circle
              placeholder="请输入.."
              button="搜索"
              style="width:300px;"
            ></k-input>
          </p>
        </demo-viewer>
      </k-col>
      <k-col span="24" :xl="{ span: 12 }">
        <demo-viewer
          title="带按钮"
          :code="DYN"
          desc="开启 button 属性，可为输入框添加操作按钮。"
        >
          <p>
            <k-input
              placeholder="请输入.."
              button="搜索"
              style="width:300px;"
            ></k-input>
          </p>
          <p>
            <k-input
              placeholder="请输入.."
              button="发送验证码"
              style="width:300px;"
            ></k-input>
          </p>
        </demo-viewer>

        <demo-viewer
          title="文本域"
          :code="WBY"
          desc="rows 属性可控制文本域默认显示的行数。"
        >
          <p>
            <k-input type="textarea" placeholder="请输入.."></k-input>
          </p>
          <p>
            <k-input type="textarea" :rows="5" placeholder="请输入.."></k-input>
          </p>
        </demo-viewer>

        <demo-viewer
          title="禁用状态"
          :code="JYZT"
          desc="disabled 属性可让输入框处于不可用状态。"
        >
          <p>
            <k-input
              disabled
              placeholder="请输入.."
              style="width:300px;"
            ></k-input>
          </p>
          <p>
            <k-input
              placeholder="请输入.."
              icon="message"
              @on-icon-click="iconClick"
              button="发送验证码"
              @on-button-click="btnClick"
              clearable
              @on-clear="handleClear"
              disabled
              style="width:300px;"
            ></k-input>
          </p>
          <p>
            <k-input
              type="textarea"
              :rows="5"
              disabled
              placeholder="请输入.."
            ></k-input>
          </p>
        </demo-viewer>
      </k-col>
    </k-row>

    <table class="doc-table">
      <caption>
        <h3>属性 props</h3>
      </caption>
      <thead>
        <tr>
          <th>参数</th>
          <th>说明</th>
          <th>类型</th>
          <th>默认值</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>type</td>
          <td>
            输入框类型，可选值为
            text、password、textarea、url、email、date、number、tel
          </td>
          <td>String</td>
          <td>text</td>
        </tr>
        <tr>
          <td>value</td>
          <td>绑定的值，可使用 v-model 双向绑定</td>
          <td>String | Number</td>
          <td>空</td>
        </tr>
        <tr>
          <td>elementId</td>
          <td>输入框的 id</td>
          <td>string</td>
          <td>-</td>
        </tr>
        <tr>
          <td>disabled</td>
          <td>设置输入框为禁用状态</td>
          <td>Boolean</td>
          <td>false</td>
        </tr>
        <tr>
          <td>readonly</td>
          <td>设置输入框为只读</td>
          <td>Boolean</td>
          <td>false</td>
        </tr>
        <tr>
          <td>placeholder</td>
          <td>占位文本</td>
          <td>String</td>
          <td>-</td>
        </tr>

        <tr>
          <td>name</td>
          <td>原生属性</td>
          <td>String</td>
          <td>—</td>
        </tr>
        <tr>
          <td>maxlength</td>
          <td>最大输入长度</td>
          <td>Number</td>
          <td>-</td>
        </tr>
        <tr>
          <td>showPwd</td>
          <td>是否显示切换密码图标</td>
          <td>Boolean</td>
          <td>false</td>
        </tr>
        <tr>
          <td>clearable</td>
          <td>是否可清空</td>
          <td>Boolean</td>
          <td>false</td>
        </tr>
        <tr>
          <td>icon</td>
          <td>输入框头部图标</td>
          <td>String</td>
          <td>-</td>
        </tr>
        <tr>
          <td>button</td>
          <td>为输入框添加操作按钮</td>
          <td>String</td>
          <td>-</td>
        </tr>
        <tr>
          <td>rows</td>
          <td>文本域默认行数，仅在 textarea 类型下有效</td>
          <td>Number</td>
          <td>2</td>
        </tr>
        <tr>
          <td>circle</td>
          <td>将使输入框设置为大圆角</td>
          <td>Boolean</td>
          <td>false</td>
        </tr>
      </tbody>
    </table>

    <table class="doc-table">
      <caption>
        <h3>事件 events</h3>
      </caption>
      <thead>
        <tr>
          <th>事件名</th>
          <th>说明</th>
          <th>返回值</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>on-icon-click</td>
          <td>设置 icon 属性后，点击图标时触发</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-button-click</td>
          <td>设置 button 属性后，点击按钮时触发</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-enter</td>
          <td>按下回车键时触发</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-keydown</td>
          <td>原生的 keydown 事件</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-keypress</td>
          <td>原生的 keypress 事件</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-keyup</td>
          <td>原生的 keyup 事件</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-focus</td>
          <td>输入框聚焦时触发</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-blur</td>
          <td>输入框失去焦点时触发</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-change</td>
          <td>数据改变时触发</td>
          <td>event</td>
        </tr>
        <tr>
          <td>on-clear</td>
          <td>开启 clearable 时可用，点击清空按钮时触发</td>
          <td>event</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
export default {
  data() {
    return {
      value1: "噗呲",
      JCYF: `
  <template>
    <k-input></k-input>
  </template>
      `,
      DYN: `
  <template>
      <k-input placeholder="请输入.." button="搜索"></k-input>
      <k-input placeholder="请输入.." button="发送验证码"></k-input>
  </template>
      `,
      ICON: `
  <template>
    <k-input
        icon="search"
        placeholder="请输入.."
        @on-icon-click="iconClick"
      ></k-input>
  </template>
      `,
      JYZT: `
  <template>
    <k-input
        disabled
        placeholder="请输入.."
      ></k-input>
    <k-input
        icon="message"
        placeholder="请输入.."
        button="发送验证码"
        disabled
      ></k-input>
  </template>
      `,
      DYCTB: `
  <template>
    <k-input showPwd placeholder="请输入.."></k-input>
  </template>
      `,
      WBY: `
  <template>
    <k-input type="textarea" placeholder="请输入.."></k-input>
    <k-input type="textarea" :rows="5" placeholder="请输入.."></k-input>
  </template>
      `,
      YJ: `
  <template>
    <k-input circle placeholder="请输入.." icon="message"></k-input>
    <k-input circle placeholder="请输入.." button="搜索"></k-input>
  </template>
      `
    };
  },
  methods: {
    iconClick() {
      console.log("icon-click");
    },
    btnClick() {
      console.log("btn-click");
    },
    handleClear() {
      console.log("clear");
    }
  }
};
</script>
